import type { PuntoResuelto, GraficoResuelto } from '@athena/shared';
/**
 * Una cifra que dice más que su número: «más de 60 %», «casi el 90 %», «<1 %». Reformatearla como
 * «60 %» quitaría el matiz, así que esas se enseñan tal como las escribe la fuente.
 */
const CIFRA_CON_MATIZ =
  /[<>~≈]|(?<!\p{L})(?:more than|less than|over|under|nearly|almost|around|about|roughly|approximately|approx|up to|at least|mehr als|weniger als|über|fast|rund|circa|environ|presque|plus de|moins de|más de|menos de|casi|cerca de|alrededor de|hasta|aprox)(?!\p{L})/iu;

/**
 * Lo que se escribe junto a un punto, **en el mismo formato que el eje y en el idioma de la
 * interfaz**: «2,6 M», «46 %», «£46». Un eje que dice «2,5 M» con un rótulo «2,60 Mio.» (la cifra
 * alemana tal cual) mezclaba dos formatos en un mismo dibujo. La cifra original de la fuente sigue en
 * la tabla, en el tooltip y en la cita.
 */
export function textoDelValor(p: PuntoResuelto, g: GraficoResuelto, idioma: string): string {
  // La marca `aproximado` la guarda la ingesta; la expresión regular cubre lo que la ingesta no marcó.
  if (g.unidad === 'proporcion' || p.aproximado || (p.cifra !== null && CIFRA_CON_MATIZ.test(p.cifra))) return p.cifra ?? String(p.valor);
  return etiquetaDelEje(p.valor, g.unidad, g.divisa, idioma, 2);
}

/** El tooltip y la tabla: el rótulo y, si difiere, la cifra tal como la escribe la fuente. */
export function textoConLaFuente(p: PuntoResuelto, g: GraficoResuelto, idioma: string): string {
  const visible = textoDelValor(p, g, idioma);
  return p.cifra === null || p.cifra === visible ? visible : `${visible} (${p.cifra})`;
}

/** El siguiente número «redondo» por encima de `max`: 1, 2, 2,5, 5 o 10 por una potencia de diez. */
export function techoDeLaEscala(max: number): number {
  if (max <= 0) return 1;
  const potencia = 10 ** Math.floor(Math.log10(max));
  const mantisa = max / potencia;
  const paso = [1, 2, 2.5, 5, 10].find(m => mantisa <= m + 1e-9)!;
  return paso * potencia;
}

const SIMBOLOS: Readonly<Record<string, string>> = { GBP: '£', EUR: '€', USD: '$' };

/** Un valor del eje, con su unidad. */
export function etiquetaDelEje(valor: number, unidad: string, divisa: string | null, idioma: string, decimales = 1): string {
  // Los millares largos («5.000.000») no caben en el margen del eje: se abrevian («5 M»).
  const abreviar = Math.abs(valor) >= 10_000 && unidad !== 'porcentaje';
  const numero = new Intl.NumberFormat(
    idioma,
    abreviar ? { notation: 'compact', maximumFractionDigits: decimales } : { maximumFractionDigits: 2 },
  ).format(valor);
  // Espacio duro, como el que pone `Intl` en «2,5 M»: la cifra y su símbolo no se parten en dos líneas.
  if (unidad === 'porcentaje') return `${numero}\u00A0%`;
  if (unidad === 'moneda') return `${SIMBOLOS[divisa ?? ''] ?? ''}${numero}${divisa !== null && SIMBOLOS[divisa] === undefined ? ` ${divisa}` : ''}`;
  return numero;
}
